import Box from "@mui/material/Box";
import { colors } from "@/theme/colors";

/**
 * The assistant's "presence" — a living orb. A conic violet→gray→tint
 * gradient slowly rotates inside a circular glass mask, wrapped in a soft
 * glow that breathes. When `active` (the assistant is thinking/streaming)
 * the spin and glow accelerate and brighten. Pure CSS; fully stilled under
 * prefers-reduced-motion.
 */
export function PresenceOrb({
  size = 44,
  active = false,
  idle = false,
}: {
  size?: number;
  /** Thinking/streaming — spins faster and the glow pulses brighter. */
  active?: boolean;
  /** Keep a slow breathe at rest. Use for the single hero orb; leave off for
   *  the many per-message avatar orbs so they stay static (cheaper). */
  idle?: boolean;
}) {
  const animate = active || idle;
  return (
    <Box
      aria-hidden
      sx={{ position: "relative", width: size, height: size, flexShrink: 0 }}
    >
      {/* Outer glow — breathes when animating. */}
      <Box
        sx={{
          position: "absolute",
          inset: `-${Math.round(size * 0.45)}px`,
          borderRadius: "50%",
          background: `radial-gradient(circle, ${colors.primary}77, ${colors.secondary}33 45%, transparent 70%)`,
          filter: "blur(12px)",
          opacity: animate ? undefined : 0.5,
          animation: animate
            ? `orbGlow ${active ? "1.7s" : "4.5s"} ease-in-out infinite`
            : "none",
          "@keyframes orbGlow": {
            "0%, 100%": { opacity: active ? 0.7 : 0.45, transform: "scale(1)" },
            "50%": { opacity: 1, transform: "scale(1.14)" },
          },
          "@media (prefers-reduced-motion: reduce)": { animation: "none" },
        }}
      />
      {/* Circular mask holding the glaze gradient (spins only when animating). */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          borderRadius: "50%",
          overflow: "hidden",
          boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.22)",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: "-30%",
            background: `conic-gradient(from 0deg, ${colors.primary}, ${colors.secondary}, ${colors.tintDeep}, ${colors.primary})`,
            animation: animate
              ? `orbSpin ${active ? "3s" : "9s"} linear infinite`
              : "none",
            "@keyframes orbSpin": { to: { transform: "rotate(360deg)" } },
            "@media (prefers-reduced-motion: reduce)": { animation: "none" },
          }}
        />
        {/* Glass sheen highlight. */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            borderRadius: "50%",
            background:
              "radial-gradient(circle at 32% 26%, rgba(255,255,255,0.55), transparent 46%)",
          }}
        />
      </Box>
    </Box>
  );
}
